/**
 * The theme rules, as pure functions. The reader's preference is one of three
 * values: follow the operating system (`system`, the default), or an explicit
 * `light` or `dark`. `theme.ts` reads the browser and draws; this file only
 * decides.
 */

/** A theme the page can show. */
export type Theme = 'light' | 'dark'

/** What the reader chose. `system` follows the operating system. */
export type ThemePreference = Theme | 'system'

/** The other theme. */
export const oppositeTheme = (theme: Theme): Theme => (theme === 'light' ? 'dark' : 'light')

/**
 * Reads a stored value. Anything that is not `light` or `dark` means the
 * reader never chose, so it follows the system. That covers `null`, an empty
 * string, and a value from a future version.
 */
export const parseStoredPreference = (stored: string | null): ThemePreference =>
  stored === 'light' || stored === 'dark' ? stored : 'system'

/** The theme the page shows for a preference, given what the system shows now. */
export const resolveTheme = (preference: ThemePreference, systemTheme: Theme): Theme =>
  preference === 'system' ? systemTheme : preference

/**
 * The preference after a press on the Dark/Light button. The press always
 * gives the opposite of what the page shows now. From `system`, that means
 * the opposite of the system theme, as an explicit choice.
 */
export const preferenceAfterTogglePress = (
  preference: ThemePreference,
  systemTheme: Theme,
): Theme => oppositeTheme(resolveTheme(preference, systemTheme))
